import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Rating,
    Skeleton,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { format } from 'date-fns';
import React from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useGetGuestProfileQuery } from '../../../app/Slices/SevenDayApiSlice';
import StarRating from '../../components/Reviews/StarRating';

const GuestProfilePage = () => {
    const { id } = useParams();
    const theme = useTheme();
    const guestId = Number(id);

    const {
        data: profile,
        isFetching,
        isError,
    } = useGetGuestProfileQuery(guestId, { skip: !guestId });

    if (isFetching) {
        return (
            <Stack spacing={3}>
                <Skeleton height={48} width={280} />
                <Skeleton height={24} width={160} />
                <Skeleton height={160} />
                <Skeleton height={160} />
            </Stack>
        );
    }

    if (isError || !profile) {
        return <Typography>Guest profile is unavailable.</Typography>;
    }

    return (
        <Stack spacing={3}>
            <Box>
                <Typography variant='h1'>{profile.name}</Typography>
                {profile.primaryContact ? (
                    <Typography variant='body2' color='text.secondary' mt={1.5}>
                        Primary contact: {profile.primaryContact}
                    </Typography>
                ) : null}
                {profile.suburb || profile.state ? (
                    <Typography variant='body2' color='text.secondary' mt={0.5}>
                        {[profile.suburb, profile.state]
                            .filter(Boolean)
                            .join(', ')}
                    </Typography>
                ) : null}
                <Stack direction='row' alignItems='center' spacing={1} mt={1.5}>
                    {profile.reviewCount > 0 ? (
                        <>
                            <StarRating
                                rating={profile.averageRating || 0}
                                size='small'
                            />
                            <Typography variant='body2'>
                                {Number(profile.averageRating || 0).toFixed(1)}{' '}
                                · {profile.reviewCount} review
                                {profile.reviewCount === 1 ? '' : 's'}
                            </Typography>
                        </>
                    ) : (
                        <Typography variant='body2' color='text.secondary'>
                            No published host reviews yet
                        </Typography>
                    )}
                </Stack>
            </Box>

            <Stack spacing={2}>
                {(profile.reviews || []).map((review) => (
                    <Card
                        key={review.id}
                        sx={{
                            border: `1px solid ${theme.palette.custom.grey}`,
                            borderRadius: '8px',
                            boxShadow: 'none',
                        }}
                    >
                        <CardContent sx={{ p: '20px' }}>
                            <Stack
                                direction='row'
                                justifyContent='space-between'
                                alignItems='flex-start'
                                mb={1}
                            >
                                <Stack direction='row' spacing={1.2}>
                                    {review?.booking?.modulePhoto?.src ? (
                                        <Avatar
                                            src={review.booking.modulePhoto.src}
                                            variant='rounded'
                                        />
                                    ) : (
                                        <AccountCircleIcon
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                color: 'primary.main',
                                            }}
                                        />
                                    )}
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: '14px',
                                            }}
                                        >
                                            {review.ownerName ||
                                                review.createdBy?.firstName ||
                                                'Host'}
                                        </Typography>
                                        <Typography sx={{ fontSize: '12px' }}>
                                            {review.booking?.moduleId &&
                                            review.booking?.moduleType ? (
                                                <Box
                                                    component={RouterLink}
                                                    to={`/viewExperiences/${review.booking.moduleType}/${review.booking.moduleId}`}
                                                    sx={{
                                                        color: 'inherit',
                                                        textDecoration:
                                                            'underline',
                                                        textUnderlineOffset:
                                                            '3px',
                                                        '&:hover': {
                                                            color: 'secondary.main',
                                                        },
                                                    }}
                                                >
                                                    {review.booking.moduleName}
                                                </Box>
                                            ) : (
                                                review.booking?.moduleName
                                            )}
                                            {review.createdAt
                                                ? ` · ${format(
                                                      new Date(
                                                          review.createdAt,
                                                      ),
                                                      'MMM yyyy',
                                                  )}`
                                                : ''}
                                        </Typography>
                                        {review.booker?.firstName ||
                                        review.booker?.lastName ? (
                                            <Typography
                                                sx={{
                                                    fontSize: '12px',
                                                    color: 'text.secondary',
                                                }}
                                            >
                                                Primary guest:{' '}
                                                {[
                                                    review.booker.firstName,
                                                    review.booker.lastName,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' ')}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                </Stack>
                                <Rating
                                    value={review.rating}
                                    readOnly
                                    size='small'
                                />
                            </Stack>
                            <Typography
                                variant='body2'
                                sx={{ fontSize: '14px', lineHeight: 1.5 }}
                            >
                                {review.reviewText}
                            </Typography>
                        </CardContent>
                    </Card>
                ))}
            </Stack>
        </Stack>
    );
};

export default GuestProfilePage;
